// Billing — credits + plan tier + usage history. Visual stub.

import { Check, CreditCard, Sparkles, Zap } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

const PLANS = [
  {
    id: "studio",
    name: "Studio",
    price: 49,
    credits: 500,
    features: ["1 brand", "Custom LoRAs", "1k images / month"],
    current: false,
  },
  {
    id: "team",
    name: "Team",
    price: 199,
    credits: 2500,
    features: [
      "5 brands",
      "Custom LoRAs",
      "5k images / month",
      "Priority support",
      "API access",
    ],
    current: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: null,
    credits: null,
    features: [
      "Unlimited brands",
      "Dedicated training",
      "SLA",
      "SSO",
      "Custom integrations",
    ],
    current: false,
  },
];

const HISTORY = [
  { id: "1", date: "May 1, 2026", amount: 199, status: "Paid" },
  { id: "2", date: "Apr 1, 2026", amount: 199, status: "Paid" },
  { id: "3", date: "Mar 1, 2026", amount: 199, status: "Paid" },
  { id: "4", date: "Feb 1, 2026", amount: 199, status: "Paid" },
];

export function BillingPage() {
  const { setToast } = useStore();

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 md:px-10 py-12 space-y-12">
      <div className="space-y-2">
        <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
          Workspace
        </p>
        <h1 className="font-freight text-5xl font-light tracking-tight leading-none">
          Billing
        </h1>
        <p className="text-muted-foreground max-w-xl">
          Plan and usage. Credits roll over month-to-month up to 2x your
          plan's allocation.
        </p>
      </div>

      {/* Credit balance + usage */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-4 w-4 text-[hsl(var(--inku-blue))]" />
            <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
              Credits this month
            </p>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-freight text-6xl font-light leading-none">
              1,847
            </span>
            <span className="text-muted-foreground">/ 2,500</span>
          </div>
          <div className="mt-5 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-[hsl(var(--inku-blue))] rounded-full"
              style={{ width: "73.9%" }}
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            73.9% used · resets June 1, 2026
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-card">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
              Card on file
            </p>
          </div>
          <p className="font-mono text-sm">•••• •••• •••• 4242</p>
          <p className="text-xs text-muted-foreground mt-1">Expires 12/28</p>
          <Button
            onClick={() =>
              setToast({
                kind: "ok",
                text: "Card management is not wired up in this demo.",
              })
            }
            variant="outline"
            size="sm"
            className="mt-4 w-full"
          >
            Update card
          </Button>
        </div>
      </section>

      {/* Plans */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Plans
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((p) => (
            <div
              key={p.id}
              className={cn(
                "rounded-xl border p-6 shadow-card flex flex-col gap-5",
                p.current
                  ? "border-[hsl(var(--inku-blue))] bg-[hsl(var(--inku-blue))/.04]"
                  : "border-border bg-card"
              )}
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
                    {p.name}
                  </p>
                  {p.current && (
                    <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--inku-blue))] text-white">
                      Current
                    </span>
                  )}
                </div>
                {p.price !== null ? (
                  <p>
                    <span className="font-freight text-4xl font-light leading-none">
                      ${p.price}
                    </span>
                    <span className="text-muted-foreground text-sm ml-1">
                      / month
                    </span>
                  </p>
                ) : (
                  <p className="font-freight text-3xl font-light leading-none italic">
                    Custom
                  </p>
                )}
                {p.credits && (
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {p.credits.toLocaleString()} credits / month
                  </p>
                )}
              </div>
              <ul className="space-y-2 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-[hsl(var(--inku-blue))] shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() =>
                  setToast({
                    kind: "ok",
                    text: "Plan changes are not wired up in this demo.",
                  })
                }
                variant={p.current ? "outline" : "default"}
                disabled={p.current}
                className="w-full"
              >
                {p.current ? "Current plan" : p.price ? "Upgrade" : "Contact sales"}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* Invoice history */}
      <section className="space-y-3">
        <div className="flex items-center gap-3">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-freight text-2xl font-light tracking-tight">
            Invoices
          </h2>
        </div>
        <div className="rounded-lg border border-border bg-card overflow-hidden shadow-card">
          {HISTORY.map((h, i) => (
            <div
              key={h.id}
              className={cn(
                "px-5 py-3.5 flex items-center justify-between text-sm",
                i < HISTORY.length - 1 && "border-b border-border"
              )}
            >
              <span className="text-muted-foreground">{h.date}</span>
              <span className="font-mono">${h.amount.toFixed(2)}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-green-700 dark:text-green-400">
                {h.status}
              </span>
              <button
                onClick={() =>
                  setToast({
                    kind: "ok",
                    text: "Invoice download not wired up in this demo.",
                  })
                }
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Download
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
